import {
  makeStyles,
  IconButton,
  Tooltip,
  withStyles,
  Link as MaterialLink,
  CircularProgress,
} from '@material-ui/core';
import CardMembershipIcon from '@material-ui/icons/CardMembership';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Specialization } from '../../api/specializations';
import venturesAPI from '../../api/ventures';
import { Venture } from '../../api/ventures/types/Venture';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { Link, Text } from '../common';
import NoIcon from '../common/no-icon';

enum SpecializationIconState {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

interface SpezializationsIconProps {
  venture: Venture;
}

interface SpezializationsTooltipProps {
  state: SpecializationIconState;
  specializations: Specialization[];
  onLoad: () => void;
}

const STooltip = withStyles(() => ({
  tooltip: {
    width: 300,
    padding: 16,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    boxShadow:
      '0px 2px 4px rgba(34, 91, 187, 0.2), 0px 4px 8px rgba(51, 126, 255, 0.12)',
    boxSizing: 'border-box',
  },
}))(Tooltip);

const useStyles = makeStyles(() => ({
  link: {
    '&:hover': {
      borderBottom: 'none',
    },
  },
  wrapper: {
    width: '100%',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  item: {
    minWidth: 30,
    backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_30,
    textAlign: 'center',
    borderRadius: 16,
    padding: 7,
  },
}));

function SpezializationsTooltip({
  state,
  specializations,
  onLoad,
}: SpezializationsTooltipProps) {
  const classes = useStyles();

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <div className={classes.wrapper}>
      {state === SpecializationIconState.LOADING ? (
        <div className={classes.loader}>
          <CircularProgress size={24} color='primary' />
        </div>
      ) : state === SpecializationIconState.ERROR ? (
        <Text variant='normal'>Something went wrong...</Text>
      ) : !specializations.length ? (
        <Text variant='normal'>No specializations</Text>
      ) : (
        <div className={classes.list}>
          {specializations.map((specialization) => (
            <div
              key={`venture-specializations-${specialization.id}`}
              className={classes.item}>
              <Text variant='normal'>{specialization.name}</Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpezializationsIcon({ venture }: SpezializationsIconProps) {
  const classes = useStyles();
  const location = useLocation();

  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [state, setState] = useState<SpecializationIconState>(
    SpecializationIconState.IDLE,
  );

  const fetchSpecializations = useCallback(async () => {
    if (state !== SpecializationIconState.IDLE) {
      return;
    }

    try {
      setState(SpecializationIconState.LOADING);
      const newSpecializations = await venturesAPI.getVentureSpecializations(
        venture.id,
      );
      setSpecializations(newSpecializations);
      setState(SpecializationIconState.SUCCESS);
    } catch (error) {
      console.error('Error fetching specializations:', error);
      setState(SpecializationIconState.ERROR);
    }
  }, [state, venture.id]);

  if (!venture.specializationsNum) {
    return <NoIcon />;
  }

  return (
    <STooltip
      title={
        <SpezializationsTooltip
          state={state}
          specializations={specializations}
          onLoad={fetchSpecializations}
        />
      }>
      <MaterialLink
        data-testid='venture-specializations-link-icon'
        onClick={(e: SyntheticEvent<any>) => e.stopPropagation()}
        component={Link}
        className={classes.link}
        to={{
          pathname: getRoutePath(Pages.VENTURE_DETAILS, {
            ventureId: venture.id,
          }),
          search: 'anchor=specializations',
          state: {
            prevPath: `${location.pathname}${location.search}`,
          },
        }}>
        <IconButton data-testid='founder-notes-icon'>
          <CardMembershipIcon />
        </IconButton>
      </MaterialLink>
    </STooltip>
  );
}

export default SpezializationsIcon;
